import { supabase } from '../lib/supabase';

// Column set returned by every public Products read. Centralized so adding a
// new column (e.g. a future `slug_aliases`) is a one-line change instead of a
// search-and-replace across three call sites.
// Lookup tables `brands` / `categories` are joined so the source-of-truth
// name flows through to the UI even if the denormalized Products.brand text
// is momentarily stale (mapProduct prefers the joined name). Products no
// longer has a `category` text column — category is category_id-only.
const PUBLIC_COLUMNS =
  'id,name,name_en,name_zh,name_ja,slug,artist,artist_id,brand,brand_id,brands(name),' +
  'category_id,categories(name),price,currency_id,' +
  'currencies(code,symbol),quantity,isPreorder,isSoldOut,isHidden,' +
  'isInTransit,displayOrder,image,imageBack,description,description_en,description_zh,description_ja,' +
  'series,series_en,series_zh,series_ja,size,features,specifications,color_variants,digital_token,release_date';

const mapProduct = (product) => ({
  id: product.id,
  name: product.name || '',
  slug: product.slug || null,
  artist: product.artist || '',
  // Prefer the joined lookup name; the denormalized text column is the fallback.
  brand: product.brands?.name || product.brand || '',
  brand_id: product.brand_id ?? null,
  category: product.categories?.name || '',
  category_id: product.category_id ?? null,
  isPreorder: product.isPreorder || false,
  isSoldOut: product.isSoldOut || false,
  isInTransit: product.isInTransit || false,
  displayOrder: product.displayOrder ?? null,
  quantity: product.quantity || 0,
  price: product.price || 0,
  currency: product.currencies?.code || product.currency || 'MXN',
  currency_id: product.currency_id || null,
  image: product.image || '',
  imageBack: product.imageBack || '',
  description: product.description || '',
  description_en: product.description_en || '',
  description_zh: product.description_zh || '',
  description_ja: product.description_ja || '',
  name_en: product.name_en || '',
  name_zh: product.name_zh || '',
  name_ja: product.name_ja || '',
  series: product.series || '',
  series_en: product.series_en || '',
  series_zh: product.series_zh || '',
  series_ja: product.series_ja || '',
  size: product.size || '',
  preorderPrice: product.preorderPrice || null,
  colorVariants: Array.isArray(product.color_variants) ? product.color_variants : [],
  digital_token: product.digital_token || null,
  release_date: product.release_date || null,
  artist_id: product.artist_id || null,
  artist_name: product.artist_name || product.artist || '',
  profile_image_url: product.profile_image_url || null,
  cover_image_url: product.cover_image_url || null,
  about: product.about || null,
  nationality: product.nationality || null,
  website_url: product.website_url || null,
  instagram_handle: product.instagram_handle || null,
  verified: product.verified || false,
  features: product.features || [],
  specifications: product.specifications || {
    material: 'N/A',
    dimension: product.size || 'N/A',
    authenticity: 'Producto auténtico',
    delivery: product.isPreorder ? 'Por confirmar' : 'Disponible',
    restrictions: null,
    policy: 'Sin reembolso ni cambio'
  }
});

/**
 * Fetch all products from Supabase
 */
export const fetchProducts = async () => {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  const supabaseKey = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

  if (!supabaseUrl || !supabaseKey || supabaseUrl === 'https://your-project.supabase.co') {
    throw new Error('Supabase no está configurado. Por favor configura REACT_APP_SUPABASE_URL y REACT_APP_SUPABASE_PUBLISHABLE_KEY en tus variables de entorno.');
  }

  const { data, error } = await supabase
    .from('Products')
    .select(PUBLIC_COLUMNS)
    .eq('isHidden', false)
    .order('id', { ascending: true });

  if (error) {
    if (error.code === 'PGRST301' || error.message?.includes('permission') || error.message?.includes('policy')) {
      throw new Error('No tienes permiso para ver los productos. Por favor verifica las políticas de seguridad (RLS) en Supabase.');
    }
    throw new Error(`Error al conectar con la base de datos: ${error.message || error.code || 'Error desconocido'}`);
  }

  return (data || []).map(mapProduct);
};

/**
 * Fetch a single product by ID
 */
export const fetchProductById = async (id) => {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  const supabaseKey = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

  if (!supabaseUrl || !supabaseKey || supabaseUrl === 'https://your-project.supabase.co') {
    throw new Error('Supabase no está configurado. Por favor configura REACT_APP_SUPABASE_URL y REACT_APP_SUPABASE_PUBLISHABLE_KEY en tus variables de entorno.');
  }

  const { data, error } = await supabase
    .from('Products')
    .select(PUBLIC_COLUMNS)
    .eq('id', id)
    .single();

  if (error || !data) {
    // Fallback: fetch all and filter in memory (handles type coercion edge cases)
    const allProducts = await fetchProducts();
    const found = allProducts.find(p =>
      p.id === id ||
      p.id === parseInt(id) ||
      String(p.id) === String(id)
    );
    if (!found) return null;
    return found;
  }

  return mapProduct(data);
};

/**
 * Fetch a single product by slug. Uses .maybeSingle() so a future duplicate
 * slug surfaces as an error we can log, instead of silently masking as 404.
 */
export const fetchProductBySlug = async (slug) => {
  if (!slug) return null;

  const { data, error } = await supabase
    .from('Products')
    .select(PUBLIC_COLUMNS)
    .eq('slug', slug)
    .maybeSingle();

  if (error) {
    console.error('[fetchProductBySlug]', slug, error);
    return null;
  }
  if (!data) return null;
  return mapProduct(data);
};
